import { useEffect, useMemo, useState } from "react";
import { getLatestTestRun,type TestRun } from "./test-results-api";
import planSource from "virtual:implementation-plan";

type ItemStatus = "complete" | "incomplete" | "progress";
type PlanScope = "launch" | "stretch";

type PlanItem = {
  status: ItemStatus;
  text: string;
  group: string | undefined;
};

type PlanSection = {
  id: number;
  title: string;
  scope: PlanScope;
  intro: string[];
  items: PlanItem[];
};

const clean = (value: string) =>
  value
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\`(.*?)\`/g, "$1")
    .replace(/\[(.*?)\]\([^)]*\)/g, "$1")
    .trim();

function parsePlan(source: string): PlanSection[] {
  const sections: PlanSection[] = [];
  let section: PlanSection | undefined;
  let group: string | undefined;

  for (const rawLine of source.split(/\r?\n/)) {
    const line = rawLine.trim();
    const sectionMatch = /^## (\d+)\.\s+(.+)$/.exec(line);
    if (sectionMatch) {
      section = { id: Number(sectionMatch[1]), title: `${sectionMatch[1]}. ${clean(sectionMatch[2]!)}`, scope: "launch", intro: [], items: [] };
      sections.push(section);
      group = undefined;
      continue;
    }

    if (line === "## Explicitly deferred from the initial release") {
      section = { id: 0, title: "Stretch goals", scope: "stretch", intro: [], items: [] };
      sections.push(section);
      group = undefined;
      continue;
    }

    if (/^## /.test(line)) {
      section = undefined;
      group = undefined;
      continue;
    }

    if (!section) continue;

    const groupMatch = /^### (.+)$/.exec(line);
    if (groupMatch) {
      group = clean(groupMatch[1]!);
      continue;
    }

    const checkbox = /^(?:\d+\.\s+|-\s+)\[([xX ])\]\s+(.+)$/.exec(line);
    if (checkbox) {
      section.items.push({
        status: checkbox[1]!.toLowerCase() === "x" ? "complete" : "incomplete",
        text: clean(checkbox[2]!),
        group
      });
      continue;
    }

    const partial = /^\*\*Partial:\*\*\s*(.+)$/.exec(line);
    if (partial) {
      section.items.push({ status: "progress", text: clean(partial[1]!), group });
      continue;
    }

    if (line && line !== "---" && !line.startsWith("- ") && !/^\d+\./.test(line)) {
      section.intro.push(clean(line));
    }
  }

  return sections.filter((item) => item.items.length > 0);
}

function count(sections: PlanSection[], status: ItemStatus) {
  return sections.reduce((total, section) => total + section.items.filter((item) => item.status === status).length, 0);
}

function summary(sections: PlanSection[]) {
  const complete = count(sections, "complete");
  const incomplete = count(sections, "incomplete");
  const progress = count(sections, "progress");
  const decided = complete + incomplete;
  return { complete, incomplete, progress, percentage: decided ? Math.round((complete / decided) * 100) : 0 };
}

function StatusSummary({ label, sections }: { label: string; sections: PlanSection[] }) {
  const totals = summary(sections);
  return (
    <section className="progression-scope-summary" aria-label={`${label} requirement status summary`}>
      <header><strong>{label}</strong><span>{totals.percentage}% complete</span></header>
      <div className="progression-summary">
        <article><span className="progress-dot complete" /><strong>{totals.complete}</strong><small>Green</small></article>
        <article><span className="progress-dot incomplete" /><strong>{totals.incomplete}</strong><small>Red</small></article>
        <article><span className="progress-dot progress" /><strong>{totals.progress}</strong><small>Grey</small></article>
      </div>
    </section>
  );
}

export function ProgressionPage() {
  const sections = useMemo(() => parsePlan(planSource), []);
  const [scopeFilter, setScopeFilter] = useState<"all" | PlanScope>("launch");
  const [filter, setFilter] = useState<"all" | ItemStatus>("all");
  const [testRun,setTestRun]=useState<TestRun|null>(null);
  useEffect(()=>{void getLatestTestRun().then(setTestRun).catch(()=>undefined);},[]);
  const launchSections = sections.filter((section) => section.scope === "launch");
  const stretchSections = sections.filter((section) => section.scope === "stretch");
  const launch = summary(launchSections);
  const visibleSections = sections
    .filter((section) => scopeFilter === "all" || section.scope === scopeFilter)
    .map((section) => ({ ...section, items: filter === "all" ? section.items : section.items.filter((item) => item.status === filter) }))
    .filter((section) => section.items.length > 0);

  return (
    <>
      <header className="team-header progression-header">
        <div>
          <p className="admin-kicker">Platform administration</p>
          <h1 className="h3 mb-1">LoadSub progression</h1>
          <p className="text-secondary mb-0">Live from the Git-versioned implementation plan.</p>
        </div>
        <span className="progression-percent">{launch.percentage}% launch complete</span>
      </header>

      <div className="progression-scope-summaries">
        <StatusSummary label="Launch" sections={launchSections} />
        <StatusSummary label="Stretch" sections={stretchSections} />
        <StatusSummary label="Overall" sections={sections} />
      </div>

      <nav className="progression-filters" aria-label="Filter progression scope">
        {([["launch","Launch"],["stretch","Stretch"],["all","Overall"]] as const).map(([value,label]) => <button type="button" className={scopeFilter===value?"active":""} aria-pressed={scopeFilter===value} onClick={()=>setScopeFilter(value)} key={value}>{label}</button>)}
      </nav>

      <nav className="progression-filters" aria-label="Filter progression requirements">
        {([["all","All"],["incomplete","Pending"],["progress","In progress"],["complete","Completed"]] as const).map(([value,label]) => <button type="button" className={filter===value?"active":""} aria-pressed={filter===value} onClick={()=>setFilter(value)} key={value}>{label}</button>)}
      </nav>

      <div className="progression-sections">
        {visibleSections.map((section, index) => {
          const done = section.items.filter((item) => item.status === "complete").length;
          const e2e = testRun?.sections.find(item => item.id === section.id);
          return (
            <details className="progression-section" key={`${section.scope}-${section.title}`} open={index === 0}>
              <summary>
                <span>{section.title}</span>
                <small>{done} / {section.items.length} complete{e2e ? ` · E2E ${e2e.status.replace("_"," ")}` : ""}</small>
              </summary>
              <div className="progression-section-body">
                {section.scope === "launch" && testRun?.hasTestBook&&<a className="btn btn-outline-primary btn-sm mb-3" href={`/admin/tests?section=${section.id}`}>Open Section {section.id} E2E report and screenshots</a>}
                {section.intro.slice(0, 2).map((line) => <p key={line}>{line}</p>)}
                <ul>
                  {section.items.map((item, itemIndex) => (
                    <li className={`progression-item ${item.status}`} key={`${item.group}-${item.text}-${itemIndex}`}>
                      <span className={`progress-dot ${item.status}`} aria-hidden="true" />
                      <div>
                        {item.group && <small>{item.group}</small>}
                        <span>{item.text}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          );
        })}
      </div>
    </>
  );
}
